import { T } from '@territory/tokens';

/*
 * variant="console" (Card, Stat, SectionHeader, ProgressBar) reads the console widget roles that products.consoles defines in
 * packages/tokens/src/themes.ts. Every read carries a base-token fallback, so the variant still renders in a product
 * without those roles; only the owner console gets the legacy look. Roles are not T tokens (no base value), which is
 * why they are read here by name and nowhere else.
 */
/* The Users drawer v2 inner palette (legacy --dv-* on #ocbDrawer), as --console-dv-* roles. Read by Button and
   IconButton variant="drawer", and by the owner app drawer compositions through drawerRole() below. */
const DV_HUES = ['em', 'cy', 'pu', 'am', 'red'] as const;
export const DRAWER_ROLES = [
  ...DV_HUES.flatMap((h) => [h, `${h}-i`, `${h}-t`, `${h}-b`] as const),
  'am-strong',
  'am-t2',
  'acc-t2',
  'surface',
  'solid',
  'border',
  'border-s',
  'shadow',
  'title',
  'sec',
  'dim',
  'btn',
  'btn-h',
  'input',
  'track',
  'pri-ink',
  'pri-shadow',
] as const;

export type DrawerRole = (typeof DRAWER_ROLES)[number];

export const CONSOLE_ROLES = [
  'card-fill',
  'card-border',
  'card-shadow',
  'card-pad',
  'tile-fill',
  'tile-shadow',
  'tile-shadow-hover',
  'tile-border-hover',
  'tile-pad',
  'tile-bar',
  'tile-bar-width',
  'tile-label-size',
  'tile-value-size',
  'tile-value-size-sm',
  /* Legacy fixed rgba fade and glow per hue (owner and admin dark). */
  ...(['neutral', 'amber', 'gold', 'blue', 'purple', 'rose'] as const).flatMap(
    (t) => [`tile-bar-${t}`, `tile-value-shadow-${t}`] as const,
  ),
  'tile-grid-gap',
  'tile-value-weight',
  'tile-value-tracking',
  'tile-sub-size',
  'tile-sub-weight',
  'tile-glow',
  'tile-glow-neutral',
  'title-glow',
  'title-bar-shadow',
  'tile-value-shadow',
  'caret-size',
  'title-size',
  'aux-size',
  'info-size',
  'chip-size',
  'chip-size-sm',
  'chip-weight',
  ...(['accent', 'amber', 'blue', 'purple', 'red'] as const).flatMap(
    (t) => [`chip-${t}-ink`, `chip-${t}-fill`, `chip-${t}-edge`] as const,
  ),
  'chip-neutral-fill',
  'chip-neutral-edge',
  'title-hue',
  'title-1',
  'title-2',
  'title-3',
  'title-4',
  'title-5',
  'title-6',
  /* Timeline, avatar (pr/ui-navdata). */
  'tl-rail',
  'tl-dot-core',
  'tl-rail-meta-size',
  'tl-rail-time-size',
  'tl-time-size',
  'tl-day-size',
  'tl-day-weight',
  'tl-day-tracking',
  'tl-hist-text',
  'tl-hist-title',
  'tl-hist-sec',
  'tl-hist-muted',
  'tl-hist-border',
  ...(['accent', 'amber', 'blue', 'purple', 'red'] as const).flatMap(
    (t) => [`tl-${t}-ink`, `tl-${t}-fill`, `tl-${t}-edge`] as const,
  ),
  'avatar-fill',
  'avatar-ink',
  'avatar-shadow',
  'avatar-ring-today',
  'avatar-ring-week',
  'avatar-ring-dormant',
  'avatar-size',
  'avatar-size-sm',
  'bar-track',
  'bar-inset',
  'bar-fill-end',
  'bar-glow',
  'pop-fill',
  'ctrl-fill',
  'exception-size',
  'navbadge-size',
  'exception-amber-edge',
  'exception-blue-edge',
  'edit-line',
  'edit-outline',
  'edit-bar-edge',
  'edit-bar-from',
  'edit-bar-to',
  'edit-ghost',
  'exception-weight',
  'dtab-size',
  'dtab-size-sm',
  'dtab-weight',
  'dtab-track',
  'dtab-ink',
  'dtab-on-fill',
  'dtab-on-edge',
  'dtab-on-shadow',
  'pop-edge',
  'pop-shadow',
  'pop-title-size',
  'pop-title-weight',
  'pop-title-tracking',
  'pop-meta-size',
  'dialog-fill',
  'dialog-edge',
  'dialog-shadow',
  'dialog-scrim',
  'dialog-rule',
  'dialog-title-size',
  'dialog-title-weight',
  'dialog-title-tracking',
  'dialog-title-leading',
  'dialog-title-pad-right',
  'dialog-body-size',
  'dialog-body-ink',
  'warn-edge',
  'drawer-page',
  'drawer-head',
  'drawer-edge',
  'drawer-shadow',
  'drawer-scrim',
  'drawer-stripe',
  'drawer-ink',
  'drawer-tab-ink',
  'drawer-tab-on',
  'drawer-tab-bar',
  'field-fill',
  'field-edge',
  'field-focus-edge',
  'field-focus-ring',
  'field-focus-outline',
  'field-placeholder',
  'inline-field-fill',
  'th-fill',
  'th-edge',
  'th-pad',
  'th-size',
  'td-pad',
  'td-edge',
  'row-stripe',
  'row-hover',
  'row-hover-bar',
  /* Modal look="panel" (legacy #pmDrawer, #otbDrawer): the drawer shadow, legacy --lift. */
  'panel-shadow',
  /* Button variant="action" (legacy .tdbtn:hover): the hover fill. Unset, the alt surface. */
  'act-hover',
  /* The third glass step as a fill for product code (legacy --surface3: avatar and presence dot wells). Unset, the alt
     surface. */
  'fill-3',
  ...DRAWER_ROLES.map((r) => `dv-${r}` as const),
  /* Pill shape="count" (legacy .onavb sidebar count badge): size, ink, fill, whole border shorthand, and the ink and fill
     while its nav item is current. Unset, the dark legacy look from base tokens. */
  'count-size',
  'count-ink',
  'count-fill',
  'count-border',
  'count-on-ink',
  'count-on-fill',
] as const;

/* Optional console roles: products.consoles (the owner look) leaves them unset and every read falls back to the owner
   value. Another console product sets them to replace a part of the owner look, e.g. the admin console's plain widget
   title (no hue bar, ink not hue). Same read rule as above. */
export const CONSOLE_OPTIONAL_ROLES = [
  'title-ink',
  'title-weight',
  'title-tracking',
  'title-bar',
  'title-rule',
  'title-pad',
  'title-gap',
  'aux-weight',
  /* Card console body leading (legacy admin light body line-height, which every widget body inherits). */
  'card-leading',
  /* Stat console tile (legacy admin .tile): label weight and tracking, label and caption leading, one value ink and one mark ink on every hue,
     and per-tone bar and value glow, whose legacy fade colors are fixed rgba values, not a mix of the live hue. */
  'tile-label-weight',
  'tile-label-tracking',
  'tile-leading',
  'tile-value-ink',
  'tile-mark-ink',
  ...(['accent', 'red'] as const).flatMap(
    (t) => [`tile-bar-${t}`, `tile-value-shadow-${t}`] as const,
  ),
  /* Timeline variant="feed": the legacy admin .feed well, .dayhead and .fitem look. */
  'feed-well-fill',
  'feed-well-edge',
  'feed-well-shadow',
  'feed-well-size',
  'feed-well-leading',
  'feed-day-ink',
  'feed-day-opacity',
  'feed-day-pad',
  'feed-day-position',
  'feed-day-fill',
  'feed-row-align',
  'feed-row-pad',
  'feed-row-edge',
  'feed-row-radius',
  'feed-row-size',
  'feed-row-hover',
  'feed-icon-col',
  'feed-icon-box',
  'feed-icon-radius',
  'feed-icon-size',
  'feed-icon-border',
  'feed-icon-shadow',
  'feed-accent-fill',
  'feed-text-size',
  'feed-name-weight',
  'feed-time-ink',
  /* ProgressBar console stacked bar (legacy admin .stackbar): corner radius, inset shade, gap between segments, and the
     segment grow-in duration. */
  'stack-radius',
  'stack-inset',
  'stack-gap',
  'stack-motion',
  'meta-size',
  /* Table console (legacy admin th, td, .tablewrap and the podium rows tr.pod1-3). */
  'th-weight',
  'th-tracking',
  'num-font',
  'td-ink',
  'td-size',
  'collapse',
  'well-fill',
  'well-edge',
  'well-radius',
  'well-shadow',
  'well-pad',
  'row-amber',
  'row-amber-hover',
  'row-amber-edge',
  'row-blue',
  'row-blue-hover',
  'row-purple',
  'row-purple-hover',
  /* Table console empty cell (legacy admin td.empty): pad, ink, size, font, tracking, the td rule and the radar mark
     (.empty::before: display, edge, fill). Unset, the base empty cell with no mark. EmptyState variant="console" reads
     the mark edge and fill too (unset, the brand emerald mix). */
  'empty-pad',
  'empty-ink',
  'empty-size',
  'empty-font',
  'empty-tracking',
  'empty-edge',
  'empty-mark',
  'empty-mark-edge',
  'empty-mark-fill',
  /* Pill rect size xs (legacy inline micro .chip) and shape badge (legacy .badge). */
  'chip-size-xs',
  'badge-size',
  'badge-ink',
  'badge-edge',
  'badge-accent-edge',
  'badge-amber-edge',
  /* Chip console (legacy .chip.sel.on). */
  'chip-on-edge',
  /* Text leading of console table cells, chips and badges (legacy admin light body line-height 1.5). */
  'leading',
  /* Console bar charts drawn in product code (legacy admin trendViz .b.email): the blue bar fades to a fixed rgba of
     the dark blue in both themes, which no base token holds. Tick and axis-max text sizes, weight and tracking
     (legacy admin light bumps .tcol .cl and .trendwrap .ymax). */
  'chart-blue-fade',
  'chart-tick-size',
  'chart-tick-weight',
  'chart-tick-tracking',
  'chart-max-size',
  /* Avatar console presence="dormant": its own fill and filter (legacy admin light .avatar.p-dormant: a slate gradient,
     no desaturate). Unset, dormant keeps the avatar fill and saturate(0.4). */
  'avatar-fill-dormant',
  'avatar-filter-dormant',
  /* Avatar console over 22px and under 29px (legacy admin inline row avatar: 26px at font-size 10px). Unset, the
     avatar-size role. */
  'avatar-size-row',
  /* Pill shape="action" (legacy .nba): a hue edge per tone where it differs from the chip edge. */
  ...(['accent', 'amber', 'blue', 'purple', 'red'] as const).map((t) => `action-${t}-edge` as const),
  /* The legacy .infoi dot (InfoTip, SectionHeader info and expand): hit box, ring inset, margins, the gap after the
     title, ring edge and ink, and both on hover. */
  'info-box',
  'info-inset',
  'info-margin',
  'info-row-gap',
  /* SectionHeader console wrapping: the row (title block and aux) and the title block (title and dots). Legacy admin
     keeps the aux beside the title and wraps the dots under it. */
  'title-wrap',
  'info-row-wrap',
  'info-edge',
  'info-ink',
  'info-edge-hover',
  'info-ink-hover',
  /* Popover console line height of the InfoTip title and window lines (legacy light inherits the body 1.5). */
  'pop-leading',
  /* Modal variant="focus" (legacy .focusback scrim, .focuscard fill, edge and shadow). */
  'focus-scrim',
  'focus-fill',
  'focus-edge',
  'focus-shadow',
  /* Stat console look="use" (legacy .usetile): tile fill and shadow, label size, weight and tracking, caption size.
     Unset, the fill and shadow are the console tile roles and the type is the legacy dark .usetile type. */
  'use-fill',
  'use-shadow',
  'use-label-size',
  'use-label-weight',
  'use-label-tracking',
  'use-sub-size',
  /* ProgressBar console look="pace" (legacy .pacebar .pb): the well inset. Unset, the console bar inset. */
  'pace-inset',
  /* ProgressRing variant="console" (legacy gauge()): track ink and opacity, center size, weight and tracking. Unset,
     the default ring values. */
  'ring-track',
  'ring-track-opacity',
  'ring-center-size',
  'ring-center-weight',
  'ring-center-tracking',
  /* ListRow variant="console" (legacy .fitem): the row hairline, the row type size and the .tx text size. Unset, the
     separator token, the inherited size and 12.5px. */
  'listrow-edge',
  'listrow-size',
  'listrow-text-size',
  /* Select console compact (legacy admin inline select, font-size:11px): the text size. Unset, 11px. */
  'field-compact-size',
] as const;

export type ConsoleRole = (typeof CONSOLE_ROLES)[number] | (typeof CONSOLE_OPTIONAL_ROLES)[number];

export const consoleVar = (name: ConsoleRole): string => `--console-${name}`;

export const consoleRole = (name: ConsoleRole, fallback: string | number): string =>
  `var(${consoleVar(name)}, ${fallback})`;

/* A drawer palette role with a base-token fallback: drawerRole('em-i', T.accent) is
   var(--console-dv-em-i, var(--rv-accent)). Outside the owner console the fallback paints. */
export const drawerRole = (name: DrawerRole, fallback: string | number): string => consoleRole(`dv-${name}`, fallback);

/* Legacy --ink-meta (products.consoles): the console meta ink for labels and aux text. */
export const consoleInkMeta = `var(--ink-meta, ${T.labelTer})`;

/* Console meta text (legacy small mono and meta lines, e.g. an email or XP line under a name): the size reads the
   optional meta-size role, the owner value 10.5px when unset. Legacy admin light bumps this text to 12px. */
export const consoleMetaSize = `var(--console-meta-size, 10.5px)`;

/* The console type ladder (legacy oc-type-css, products.consoles --type-*): consoleType('fs', 'meta', '12px') is the
   meta size, 12px dark and 12.5px light. kind: fs size, fw weight, lh line height, ls tracking, sp spacing. Outside the
   owner console the fallback paints. */
export type ConsoleTypeKind = 'fs' | 'fw' | 'lh' | 'ls' | 'sp';
export const consoleType = (kind: ConsoleTypeKind, name: string, fallback: string | number): string => `var(--type-${kind}-${name}, ${fallback})`;

/* The console ink levels (legacy --ink-*, products.consoles): title, heading, body, sub, meta, caption, num and the
   quote rule. Each reads the product ink, else the base token given. */
export type ConsoleInkLevel = 'title' | 'heading' | 'body' | 'sub' | 'meta' | 'caption' | 'num' | 'quote-rule';
export const consoleInk = (level: ConsoleInkLevel, fallback: string): string => `var(--ink-${level}, ${fallback})`;

/* Console caption size (legacy .selfnote and .rtools .cnt): the consoles type role --type-fs-caption, 11px dark and
   12px light (the owner light small-font floor), 11px where the role is unset. */
export const consoleCaptionSize = `var(--type-fs-caption, 11px)`;

/* Fallbacks for the card title hue rotation: the base hues in the legacy order. */
export const CONSOLE_TITLE_FALLBACK = [T.accent, T.blue, T.purple, T.amber, T.rose, T.gold] as const;

export type ConsoleTitleHue = 1 | 2 | 3 | 4 | 5 | 6;

export const consoleTitleHue = (n: ConsoleTitleHue): string =>
  consoleRole(`title-${n}` as ConsoleRole, CONSOLE_TITLE_FALLBACK[n - 1]);

/* Hue a console SectionHeader paints with: set by its console Card (position rotation or titleHue), else accent. */
export const consoleHeaderHue = consoleRole('title-hue', T.accent);

export const glowMix = (color: string, role: 'tile-glow' | 'tile-glow-neutral' | 'title-glow'): string =>
  `color-mix(in srgb, ${color} ${consoleRole(role, '0%')}, transparent)`;

/* Button variant="console" (legacy .btn) reads the shell chrome button roles in products.consoles, which the /next/
   top bar already paints with. Same rule: read here by name only, each with a base-token fallback. */
export const CONSOLE_BUTTON_ROLES = [
  'btn',
  'btn-edge',
  'btn-shadow',
  'btn-hover',
  'btn-hover-edge',
  'btn-hover-shadow',
  'btn-accent',
  'btn-accent-ink',
  'btn-accent-edge',
  'btn-accent-shadow',
  'btn-accent-hover',
  'btn-accent-hover-shadow',
  'btn-accent-hover-filter',
  'btn-danger-ink',
  'btn-danger-edge',
  'btn-danger-hover-edge',
  'btn-disabled-opacity',
] as const;

/* Light only: legacy light .btn:disabled greys the button; dark keeps the tone's own fill, ink, edge and shadow. */
export const CONSOLE_BUTTON_LIGHT_ROLES = ['btn-disabled', 'btn-disabled-ink', 'btn-disabled-edge', 'btn-disabled-shadow'] as const;

/* Optional, as CONSOLE_OPTIONAL_ROLES: unset in products.consoles. btn-size replaces the size scale (legacy admin light
   sets every .btn to one size); btn-accent-weight replaces the accent tone's weight. */
export const CONSOLE_BUTTON_OPTIONAL_ROLES = ['btn-size', 'btn-accent-weight'] as const;

export type ConsoleButtonRole =
  | (typeof CONSOLE_BUTTON_ROLES)[number]
  | (typeof CONSOLE_BUTTON_LIGHT_ROLES)[number]
  | (typeof CONSOLE_BUTTON_OPTIONAL_ROLES)[number];

export const chromeButtonRole = (name: ConsoleButtonRole, fallback: string): string => `var(--chrome-${name}, ${fallback})`;

/* Pill variant="console" (legacy .pill, .pill.live, .dot) reads the shell chrome pill roles in products.consoles, which
   the /next/ top bar paints with. Same rule: read here by name only, each with a base-token fallback. */
export const CONSOLE_PILL_ROLES = [
  'pill',
  'pill-edge',
  'pill-size',
  'pill-weight',
  'live',
  'live-ink',
  'live-edge',
  'live-glow-lo',
  'live-glow-hi',
  'dot',
  'dot-glow',
] as const;

export type ConsolePillRole = (typeof CONSOLE_PILL_ROLES)[number];

export const chromePillRole = (name: ConsolePillRole, fallback: string): string => `var(--chrome-${name}, ${fallback})`;
